import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { LabeledCheckbox } from './ui/labeled-checkbox';

/** Debounce before the search box asks the server for a narrowed value list. */
const SEARCH_DEBOUNCE_MS = 300;

interface ColumnFilterMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;

  /** The column's name, shown as the menu's heading - the header itself may only carry an icon. */
  title: string;

  /** Sort direction currently applied to this column, null when another column is sorted. */
  sortDirection: 'asc' | 'desc' | null;
  onSort: (direction: 'asc' | 'desc') => void;
  /** Picking the direction the column already has hands it back, to drop the sort entirely. */
  onClearSort: () => void;

  /** The values to tick, as the server returned them for the current search. */
  values: string[];
  /** How many distinct values the search holds in total; larger than values when the list is capped. */
  totalCount: number;
  isLoading: boolean;

  /** Called debounced while typing in the search box, so the list comes from the server. */
  onSearch: (term: string) => void;

  /** Label for the blanks entry ("(No sow)"), or undefined when the set has no blank rows. */
  blankLabel?: string;

  selectedValues: string[];
  blankSelected: boolean;

  /** OK. Called with the whole selection; an empty selection means "no filter on this column". */
  onApply: (values: string[], blankSelected: boolean) => void;
}

/**
 * An Excel-style column header menu: sort both ways, a search box, and a checkbox list of the
 * column's values with (Select All).
 *
 * The selection is applied on OK, not on each click - the list is server-paged and every change
 * would otherwise re-run the search. A Popover rather than a Menu, because MUI's Menu keyboard
 * typeahead swallows what is typed into the search box - the sort entries still need their own
 * MenuList, since a v9 MenuItem throws without the context a Menu/MenuList provides.
 */
const ColumnFilterMenu = ({
  anchorEl,
  onClose,
  title,
  sortDirection,
  onSort,
  onClearSort,
  values,
  totalCount,
  isLoading,
  onSearch,
  blankLabel,
  selectedValues,
  blankSelected,
  onApply
}: ColumnFilterMenuProps) => {
  const { t } = useTranslation();

  const open = Boolean(anchorEl);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [draftValues, setDraftValues] = React.useState<string[]>([]);
  const [draftBlank, setDraftBlank] = React.useState(false);

  // Opening the menu starts from what is applied, so Cancel can simply close.
  React.useEffect(() => {
    if (open) {
      setSearchTerm('');
      setDraftValues(selectedValues);
      setDraftBlank(blankSelected);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handle = setTimeout(() => onSearch(searchTerm), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [searchTerm, open]);

  const isCapped = totalCount > values.length;
  const hasList = values.length > 0 || blankLabel !== undefined;
  const draftCount = draftValues.length + (draftBlank ? 1 : 0);

  // Only what is on screen counts as "all": the search box narrows the list, and Excel's
  // (Select All) ticks the narrowed list rather than the whole column.
  const allListedSelected =
    values.every((value) => draftValues.includes(value)) && (blankLabel === undefined || draftBlank);
  const someListedSelected =
    values.some((value) => draftValues.includes(value)) || (blankLabel !== undefined && draftBlank);

  const toggleAll = () => {
    const select = !allListedSelected;

    // A value the search box is currently hiding stays as it was - unticking (Select All) while a
    // search is typed must not silently drop a selection the user cannot see.
    setDraftValues(
      select ? [...new Set([...draftValues, ...values])] : draftValues.filter((value) => !values.includes(value))
    );

    if (blankLabel !== undefined) {
      setDraftBlank(select);
    }
  };

  const toggleValue = (value: string) => (checked: boolean) => {
    setDraftValues(checked ? [...draftValues, value] : draftValues.filter((it) => it !== value));
  };

  const apply = () => {
    onApply(draftValues, draftBlank);
    onClose();
  };

  const clear = () => {
    onApply([], false);
    onClose();
  };

  const renderSortItem = (direction: 'asc' | 'desc', icon: JSX.Element, labelKey: string) => {
    const isActive = sortDirection === direction;

    const item = (
      <MenuItem
        selected={isActive}
        onClick={() => (isActive ? onClearSort() : onSort(direction))}
        className="!py-2 !text-sm"
      >
        <Box className="mr-2 flex text-gray-500">{icon}</Box>
        <span className={isActive ? 'font-semibold text-blue-700' : undefined}>{t(labelKey)}</span>
        {isActive && <CheckIcon fontSize="small" className="!ml-auto !text-blue-700" />}
      </MenuItem>
    );

    // The sort a search is saved with comes back with it, so the way out of one has to be here.
    return isActive ? <Tooltip title={t('f-column-filter-sort-remove')}>{item}</Tooltip> : item;
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { className: '!w-80 !mt-1 !rounded-xl !border !border-gray-200 !shadow-lg' } }}
    >
      <Box className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
        <Typography variant="subtitle2" className="!font-semibold text-gray-700">
          {title}
        </Typography>
        {draftCount > 0 && (
          <Chip
            size="small"
            label={t('f-column-filter-selected', { selected: draftCount })}
            className="!bg-blue-100 !text-blue-800"
          />
        )}
      </Box>

      <MenuList disablePadding className="!py-1">
        {renderSortItem('asc', <ArrowUpwardIcon fontSize="small" />, 'f-column-filter-sort-asc')}
        {renderSortItem('desc', <ArrowDownwardIcon fontSize="small" />, 'f-column-filter-sort-desc')}
      </MenuList>

      <Divider />

      <Box className="px-4 pt-3">
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          autoFocus
          placeholder={t('f-com-search')}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              apply();
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" className="text-gray-400" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <Tooltip title={t('f-column-filter-clear-search')}>
                    <IconButton size="small" edge="end" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : null
            }
          }}
        />
      </Box>

      {!isLoading && hasList && (
        <Box className="border-b border-gray-100 px-4 pt-1">
          <LabeledCheckbox
            className="!flex !py-1.5 !text-sm font-medium"
            checked={allListedSelected ? true : someListedSelected ? 'indeterminate' : false}
            onCheckedChange={toggleAll}
            label={t('f-column-filter-select-all')}
          />
        </Box>
      )}

      <Box className="max-h-56 overflow-y-auto px-4 py-1">
        {isLoading && (
          <Box className="flex justify-center py-6">
            <CircularProgress size={22} />
          </Box>
        )}

        {!isLoading && !hasList && (
          <Typography variant="body2" className="!py-4 text-center text-gray-500">
            {t(searchTerm ? 'f-column-filter-no-matches' : 'f-column-filter-no-values')}
          </Typography>
        )}

        {!isLoading && hasList && (
          <>
            {blankLabel !== undefined && (
              <LabeledCheckbox
                className="!flex !py-1.5 !text-sm !text-gray-500 italic"
                checked={draftBlank}
                onCheckedChange={setDraftBlank}
                label={blankLabel}
              />
            )}

            {values.map((value) => (
              <LabeledCheckbox
                key={value}
                className="!flex !py-1.5 !text-sm"
                checked={draftValues.includes(value)}
                onCheckedChange={toggleValue(value)}
                label={<span className="block max-w-56 truncate">{value}</span>}
              />
            ))}
          </>
        )}
      </Box>

      {!isLoading && isCapped && (
        <Typography variant="caption" className="!block border-t border-gray-100 px-4 py-1 text-gray-500">
          {/* Not named `count`: that key switches i18next to plural resolution. */}
          {t('f-column-filter-capped', { shown: values.length, total: totalCount })}
        </Typography>
      )}

      <Box className="flex items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2">
        <Button size="small" color="inherit" className="!text-gray-600" onClick={clear}>
          {t('f-column-filter-clear')}
        </Button>
        <Box className="flex gap-1">
          <Button size="small" color="inherit" className="!text-gray-600" onClick={onClose}>
            {t('f-com-cancel')}
          </Button>
          <Button size="small" variant="contained" disableElevation onClick={apply}>
            {t('f-com-ok')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default ColumnFilterMenu;
